import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, FormGroup, Label, FormFeedback, Col, Row, Alert, TabPane } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import ButtonIcon from '../../common/ButtonIcon';
import CredsModal from '../Vendor/CredsModal';
import DynamicCredentialField from './DynamicCredentialField';
import DynamicPostCredWidget from './DynamicPostCredWidget';
import BZAPIService from '../../../services/BZAPIService';
import { resolveSource } from './schemaTransforms';

/**
 * Renders the credentials tab (Tab 2) from a schema definition.
 * Implements the full credential UX flow: collapsed/expanded, test, save, stored status.
 */
const DynamicCredentialsTab = ({
  schema,
  vendorId,
  fields,
  handleFieldChange,
  isSubmitted,
  activeTab,
  dynamicState,
  setDynamicState,
  // Props passed through from parent
  ...restProps
}) => {
  const [isValidCredentials, setIsValidCredentials] = useState(false);
  const [isTestingValidCredentials, setIsTestingValidCredentials] = useState(false);
  const [isAddingCredentials, setIsAddingCredentials] = useState(false);

  if (!schema) return null;

  const credFields = schema.fields || [];
  const storedCheckField = schema.storedCheckField;
  const hasStoredCreds = storedCheckField && (fields[storedCheckField] || isValidCredentials);

  /**
   * Tests credentials via the vendor test endpoint, executing onSuccess actions.
   */
  async function testCreds() {
    setIsTestingValidCredentials(true);
    try {
      // Build test payload from schema
      const testConfig = schema.testEndpoint;
      const data = {};
      if (testConfig.payload && typeof testConfig.payload === 'object') {
        // Object format: { "payloadKey": "fields.fieldId" }
        Object.entries(testConfig.payload).forEach(([key, source]) => {
          data[key] = resolveSource(source, { fields, ...restProps }, dynamicState);
        });
      } else {
        // Array format: [{ key, source, transform }]
        (testConfig.buildPayload || []).forEach(item => {
          const val = resolveSource(item.source, { fields, ...restProps }, dynamicState);
          if (item.transform === 'boolToInt') {
            data[item.key] = val === false ? 0 : 1;
          } else {
            data[item.key] = val;
          }
        });
      }

      const result = await BZAPIService.vendorTestCreds(vendorId, data);
      setIsTestingValidCredentials(false);

      if (!result) {
        toast.error(testConfig.errorMessage || 'Unable to validate credentials.');
        setIsAddingCredentials(false);
        return;
      }

      // Execute onSuccess actions
      let hasError = false;
      (testConfig.onSuccess || []).forEach(action => {
        switch (action.action) {
          case 'checkField':
            if (!result[action.field]) {
              toast.error(action.errorMessage || 'Validation failed.');
              hasError = true;
            }
            break;
          case 'setStateFromResult': {
            const raw = result[action.resultPath || action.resultField];
            // Honor the schema's declared option format. The Resilience Hub test
            // endpoint returns resHubAppNames as a string[] (regions already as
            // {label,value}); 'labelValue' normalizes both into the {label,value}
            // shape the multi-selects render, so option labels aren't blank. #454
            const value =
              action.format === 'labelValue' && Array.isArray(raw)
                ? raw.map(item =>
                    item && typeof item === 'object'
                      ? { label: item.label ?? item.value, value: item.value ?? item.label }
                      : { label: item, value: item }
                  )
                : raw;
            setDynamicState(prev => ({ ...prev, [action.stateKey]: value }));
            break;
          }
          case 'setFieldFromResult':
            {
              const resultKey = action.resultPath || action.resultField;
              const fieldKey = action.fieldId || action.fieldKey;
              if (fields && result[resultKey]) {
                fields[fieldKey] = result[resultKey];
              }
            }
            break;
          default:
            break;
        }
      });

      if (!hasError && (result.completed !== undefined ? result.completed : true)) {
        setIsValidCredentials(true);
        setIsAddingCredentials(false);
        toast.success(testConfig.successMessage || 'Credentials validated.');
      } else if (!hasError) {
        toast.error(testConfig.errorMessage || 'Unable to validate credentials.');
        setIsAddingCredentials(false);
      }
    } catch (e) {
      setIsTestingValidCredentials(false);
      setIsAddingCredentials(false);
      toast.error(schema?.testEndpoint?.errorMessage || 'Unable to validate credentials.');
      console.error('Credential test error:', e);
    }
  }

  /**
   * Handles dynamic state changes from post-credential widgets.
   */
  function handleWidgetChange(stateKey, value) {
    setDynamicState(prev => ({ ...prev, [stateKey]: value }));
  }

  /**
   * Determines if a post-credential widget should be visible.
   */
  function isWidgetVisible(widget) {
    if (!widget.condition) return true;
    const val = dynamicState[widget.condition] || restProps[widget.condition];
    return !!val;
  }

  /**
   * Resolves widget options from schema definition.
   */
  function getWidgetOptions(widget) {
    if (widget.optionsSource === 'static') {
      return widget.options || [];
    }
    if (widget.optionsSource) {
      const dynamicOpts = dynamicState[widget.optionsSource] || restProps[widget.optionsSource];
      if (dynamicOpts && widget.optionsTransform === 'awsOrgTransform') {
        // Transform raw org data to select options
        return Array.isArray(dynamicOpts)
          ? dynamicOpts.map(o => ({ label: o.name || o.label || o, value: o.id || o.value || o }))
          : [];
      }
      return dynamicOpts || [];
    }
    return widget.options || [];
  }

  const allCredsEmpty = credFields.every(f => !fields[f.id]);

  return (
    <TabPane tabId="2">
      {activeTab === '2' && (
        <Card bg={'light'} text={'dak'} className="mb-4">
          <CardBody>
            <Row form>
              <Col lg="12">
                <FormGroup>
                  <Label>
                    {schema.title || 'Credentials'}
                    <span style={{ color: '#bd472a' }}>*</span>
                  </Label>
                  {!isAddingCredentials ? (
                    <>
                      <div className="d-flex align-items-center">
                        <ButtonIcon
                          onClick={() => setIsAddingCredentials(true)}
                          color="falcon-default"
                          data-testid={'dynamic-set-credentials-btn'}
                          size="sm"
                          icon="plus"
                          iconClassName="fs--2"
                        >
                          Set Credentials
                        </ButtonIcon>

                        {hasStoredCreds ? (
                          <div className="d-flex align-items-center" data-testid={'dynamic-creds-stored-message'}>
                            {'  '}
                            <FontAwesomeIcon icon={'check'} style={{ color: 'green', marginLeft: '10px' }} />{' '}
                            {schema.storedMessage || 'Credentials Stored'}
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Render credential input fields in rows of 2 */}
                      {renderFieldRows(credFields, fields, handleFieldChange, isSubmitted)}

                      <CredsModal
                        isTestingValidCredentials={isTestingValidCredentials}
                        setIsTestingCredentials={setIsTestingValidCredentials}
                        fields={fields}
                        testCreds={testCreds}
                        vendorId={vendorId}
                      />
                      <ButtonIcon
                        onClick={() => setIsAddingCredentials(false)}
                        color="falcon-default"
                        size="sm"
                        icon="times"
                        iconClassName="fs--2"
                        style={{ marginRight: '10px' }}
                        data-testid={'dynamic-cancel-creds-btn'}
                      >
                        Cancel
                      </ButtonIcon>
                    </>
                  )}
                  <FormFeedback className={allCredsEmpty && isSubmitted ? 'd-block' : ''} type="invalid">
                    <Alert color="danger" fade={false}>
                      Please add required credentials.
                    </Alert>
                  </FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            {/* Post-credential widgets */}
            {(schema.postCredentialWidgets || []).length > 0 && (
              <Row form>
                {schema.postCredentialWidgets.map((widget, i) => (
                  <DynamicPostCredWidget
                    key={widget.stateKey || i}
                    widget={widget}
                    value={dynamicState[widget.stateKey] || restProps[widget.stateKey]}
                    options={getWidgetOptions(widget)}
                    onChange={handleWidgetChange}
                    isSubmitted={isSubmitted}
                    isVisible={isWidgetVisible(widget)}
                  />
                ))}
              </Row>
            )}
          </CardBody>
        </Card>
      )}
    </TabPane>
  );
};

/**
 * Renders credential fields in rows, pairing fields by their colSize.
 */
function renderFieldRows(credFields, fields, handleFieldChange, isSubmitted) {
  const rows = [];
  let currentRow = [];
  let currentRowSize = 0;

  credFields.forEach((field, i) => {
    const size = field.colSize || 6;
    if (currentRowSize + size > 12) {
      rows.push(currentRow);
      currentRow = [];
      currentRowSize = 0;
    }
    currentRow.push(field);
    currentRowSize += size;
  });
  if (currentRow.length) rows.push(currentRow);

  return rows.map((row, ri) => (
    <Row key={ri}>
      {row.map(field => (
        <DynamicCredentialField
          key={field.id}
          field={field}
          value={fields[field.id]}
          onChange={handleFieldChange}
          isSubmitted={isSubmitted}
        />
      ))}
    </Row>
  ));
}

/**
 * Evaluates a secret condition string against fields.
 * Supports "fields.fieldA && fields.fieldB" pattern.
 */
function evaluateSecretCondition(condition, fields) {
  if (!condition) return false;
  // Object format: { "anyField": ["fieldA", "fieldB"] }
  if (typeof condition === 'object' && condition.anyField) {
    return condition.anyField.some(fieldName => !!fields[fieldName]);
  }
  // String format: "fields.a && fields.b"
  if (typeof condition === 'string') {
    const parts = condition.split('&&').map(p => p.trim());
    return parts.every(part => {
      const match = part.match(/^fields\.(\w+)$/);
      if (match) return !!fields[match[1]];
      return false;
    });
  }
  return false;
}

/**
 * Builds a secret payload from a schema definition, resolving "fields.X" references.
 */
function buildSecretPayload(payloadDef, fields) {
  const payload = {};
  Object.entries(payloadDef).forEach(([key, val]) => {
    if (typeof val === 'object' && !Array.isArray(val)) {
      payload[key] = {};
      Object.entries(val).forEach(([innerKey, innerVal]) => {
        if (typeof innerVal === 'string' && innerVal.startsWith('fields.')) {
          payload[key][innerKey] = fields[innerVal.replace('fields.', '')];
        } else {
          payload[key][innerKey] = innerVal;
        }
      });
    } else if (typeof val === 'string' && val.startsWith('fields.')) {
      payload[key] = fields[val.replace('fields.', '')];
    } else {
      payload[key] = val;
    }
  });
  return payload;
}

DynamicCredentialsTab.propTypes = {
  schema: PropTypes.object.isRequired,
  vendorId: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
  activeTab: PropTypes.string,
  dynamicState: PropTypes.object,
  setDynamicState: PropTypes.func
};

export { evaluateSecretCondition, buildSecretPayload };
export default DynamicCredentialsTab;
